import DesktopDatePicker from "@mui/lab/DesktopDatePicker";
import TimePicker from "@mui/lab/DesktopTimePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";

import DateAdapter from "@mui/lab/AdapterMoment";
import TextField from "@mui/material/TextField";
import moment, { Moment } from "moment-timezone";
import { RecurrenceRange, RecurrenceType } from "types/Event";
import { DATE_TIME_FORMATS } from "constant";
import { getInputTimeFormat } from "utils/datetime";

export interface RangeOfRecurrenceTemplateProps {
    recurrence: RecurrenceType;
    recurrenceRange: RecurrenceRange;
    setRecurrenceRange: (recurrenceRange: RecurrenceRange) => void;
    renderErrorMsg: (
        component: RecurrenceType,
        field: "repeat" | "range" | "timeSchedule",
        day?: string,
        month?: string
    ) => JSX.Element | undefined;
    recurrenceErrorMsg: string;
    updateRecurrenceErrorMsg: (recurrenceErrorMsg: string) => void;
}

export const RangeOfRecurrenceTemplate = ({
    recurrence,
    recurrenceRange,
    setRecurrenceRange,
    renderErrorMsg,
    recurrenceErrorMsg,
    updateRecurrenceErrorMsg,
}: RangeOfRecurrenceTemplateProps) => {
    const START_DATE = "start_date";
    const END_DATE = "end_date";
    const ENABLE_END_DATE = true;
    const [startDate, setStartDate] = useState<moment.Moment>(moment());
    const [startTime, setStartTime] = useState<moment.Moment>(moment());
    const [endDate, setEndDate] = useState<moment.Moment>(moment());
    const [endTime, setEndTime] = useState<moment.Moment>(moment());
    const [startTimeError, setStartTimeError] = useState<string>("");
    const [endTimeError, setEndTimeError] = useState<string>("");

    useEffect(() => {
        setStartDate(recurrenceRange[recurrence].startDate);
        setEndDate(recurrenceRange[recurrence].endDate);
        setStartTime(recurrenceRange[recurrence].startDate);
        setEndTime(recurrenceRange[recurrence].endDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        updateRecurrenceErrorMsg(startTimeError || endTimeError);
    }, [startTimeError, endTimeError, updateRecurrenceErrorMsg]);

    const renderDate = (startOrEnd: any, enableEndDate?: any) => {
        const handleDateChange = (dateChanged: Moment | null) => {
            if (!dateChanged) return;

            let updatedData: any = {};
            if (startOrEnd === START_DATE) {
                setStartDate(dateChanged);
                updatedData.startDate = moment().set({
                    year: dateChanged.year(),
                    month: dateChanged.month(),
                    date: dateChanged.date(),
                    hour: startTime?.hour(),
                    minute: startTime?.minute(),
                });
            } else {
                setEndDate(dateChanged);
                updatedData.endDate = moment().set({
                    year: dateChanged.year(),
                    month: dateChanged.month(),
                    date: dateChanged.date(),
                    hour: endTime?.hour(),
                    minute: endTime?.minute(),
                });
            }

            setRecurrenceRange({
                ...recurrenceRange,
                [recurrence]: {
                    ...recurrenceRange[recurrence],
                    ...updatedData,
                },
            });
        };

        const handleTimeChange = (dateChanged: Moment | null) => {
            if (!dateChanged?.isValid()) {
                if (startOrEnd === START_DATE) {
                    setStartTimeError("Invalid start time format.");
                } else {
                    setEndTimeError("Invalid end time format.");
                }
                return;
            }

            let updatedData: any = {};
            if (startOrEnd === START_DATE) {
                setStartTime(dateChanged);
                setStartTimeError("");
                updatedData.startDate = moment().set({
                    year: startDate?.year(),
                    month: startDate?.month(),
                    date: startDate?.date(),
                    hour: dateChanged.hour(),
                    minute: dateChanged.minute(),
                });
            } else {
                setEndTime(dateChanged);
                setEndTimeError("");
                updatedData.endDate = moment().set({
                    year: endDate?.year(),
                    month: endDate?.month(),
                    date: endDate?.date(),
                    hour: dateChanged.hour(),
                    minute: dateChanged.minute(),
                });
            }

            setRecurrenceRange({
                ...recurrenceRange,
                [recurrence]: {
                    ...recurrenceRange[recurrence],
                    ...updatedData,
                },
            });
        };

        if (enableEndDate) {
            return (
                <LocalizationProvider dateAdapter={DateAdapter}>
                    <div className="date-and-time-picker mt-3">
                        <DesktopDatePicker
                            label="Date desktop"
                            inputFormat={DATE_TIME_FORMATS.DATE_ONLY}
                            value={recurrenceRange[recurrence].endDate}
                            onChange={handleDateChange}
                            minDate={moment("2022-01-01")}
                            renderInput={(props) => (
                                <TextField
                                    {...props}
                                    label=""
                                    InputLabelProps={{
                                        shrink: false,
                                    }}
                                />
                            )}
                        />

                        <TimePicker
                            label="Time"
                            inputFormat={getInputTimeFormat()}
                            value={recurrenceRange[recurrence].endDate}
                            onChange={handleTimeChange}
                            renderInput={(props) => (
                                <TextField
                                    {...props}
                                    label=""
                                    InputLabelProps={{
                                        shrink: false,
                                    }}
                                    error={endTimeError !== ""}
                                />
                            )}
                        />
                    </div>
                </LocalizationProvider>
            );
        }

        return (
            <LocalizationProvider dateAdapter={DateAdapter}>
                <div className="date-and-time-picker">
                    <DesktopDatePicker
                        label="Date desktop"
                        inputFormat="DD/MM/YYYY"
                        value={recurrenceRange[recurrence].startDate}
                        onChange={(newValue: any) => {
                            handleDateChange(newValue);
                        }}
                        minDate={moment("2022-01-01")}
                        renderInput={(props) => (
                            <TextField
                                {...props}
                                label=""
                                InputLabelProps={{
                                    shrink: false,
                                }}
                            />
                        )}
                    />

                    <TimePicker
                        label="Time"
                        inputFormat={getInputTimeFormat()}
                        value={recurrenceRange[recurrence].startDate}
                        onChange={handleTimeChange}
                        renderInput={(props) => (
                            <TextField
                                {...props}
                                label=""
                                InputLabelProps={{
                                    shrink: false,
                                }}
                                error={startTimeError !== ""}
                            />
                        )}
                    />
                </div>
            </LocalizationProvider>
        );
    };

    return (
        <div className="form-box mb-3">
            <h5 className="mb-4">Range of Recurrence</h5>
            <div className="recurrence-start-time">
                <p>Start</p>
                {renderDate(START_DATE)}
            </div>
            <div className="recurrence-end-time-option">
                <p>End</p>
                <div className="end-time-check">
                    <Form.Check
                        name="end"
                        id={"no_end"}
                        custom
                        checked={
                            recurrenceRange[recurrence].endDateEnabled === false
                        }
                        type="radio"
                        label={<span className="ml-2">No end date</span>}
                        onChange={(e: any) => {
                            setRecurrenceRange({
                                ...recurrenceRange,
                                [recurrence]: {
                                    ...recurrenceRange[recurrence],
                                    endDateEnabled: false,
                                },
                            });
                            setEndTimeError("");
                            updateRecurrenceErrorMsg(""); // Clear the parent's error message
                        }}
                    />
                    <Form.Check
                        name="end"
                        id={"end_by"}
                        custom
                        checked={
                            recurrenceRange[recurrence].endDateEnabled === true
                        }
                        type="radio"
                        label={<span className="ml-2">End by</span>}
                        onChange={(e: any) => {
                            setRecurrenceRange({
                                ...recurrenceRange,
                                [recurrence]: {
                                    ...recurrenceRange[recurrence],
                                    endDateEnabled: true,
                                },
                            });
                            setEndTimeError("");
                        }}
                    />
                </div>
            </div>
            <div className="recurrence-start-time">
                <div></div>
                {recurrenceRange[recurrence].endDateEnabled
                    ? renderDate(END_DATE, ENABLE_END_DATE)
                    : ""}
            </div>
            {recurrenceErrorMsg && (
                <div className="recurrence-start-time mt-2">
                    <div></div>
                    <span className="text-danger">{recurrenceErrorMsg}</span>
                </div>
            )}

            {renderErrorMsg(recurrence, "range")}
        </div>
    );
};
